import {
  BodyText,
  Button,
  Callout,
  HeaderText,
  Skeleton,
  Switch,
  Tooltip,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking/tracking";
import { cn } from "@/lib/utils";
import {
  getActionUnavailableReasons,
  isActionAvailable,
} from "@/utils/available-actions";
import { Condition } from "@curai/js-fhir-tools";
import type * as fhirTypes from "fhir/r4";
import { useAtom, useSetAtom } from "jotai";
import { LoaderCircle } from "lucide-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { CarePlanPreviewModal } from "../../CarePlanPreview/CarePlanPreviewModal";
import { amazonCarePlanPreviewAtom } from "../../atoms/amazonCarePlanPreview";
import { asyncRxCarePlanPreviewAtom } from "../../atoms/asyncRxCarePlanPreview";
import { Dispositions } from "../Disposition/Dispositions";
import { FollowUps } from "../FollowUp/FollowUps";
import { Labs } from "../Labs/Labs";
import { Recommendations } from "../Recommendations/Recommendations";
import { Referral } from "../Referrals/Referral";
import { Medications } from "../Rx/Medications";
import {
  CareplanAvailableAction,
  CareplanAvailableActionsUnavailabilityReason,
  useCarePlanSummary,
} from "../network/useCarePlanSummary";
import {
  ChartingAvailableAction,
  useChartingAvailableActions,
} from "../network/useChartingAvailableActions";
import { useCreateCarePlan } from "../network/useCreateCarePlan";
import { useToggleReferOut } from "../network/useToggleReferOut";

interface CarePlanProps {
  patientId: string;
  encounterId: string;
  motherEncounterId: string;
  condition?: fhirTypes.Condition;
  episodeOfCareId?: string;
  isAmazon?: boolean;
  enabled?: boolean;
}

export const CarePlan = ({
  patientId,
  encounterId,
  motherEncounterId,
  condition,
  episodeOfCareId,
  isAmazon = false,
  enabled = true,
}: CarePlanProps) => {
  const [isReferral, setIsReferral] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [, setAmazonCarePlanPreview] = useAtom(amazonCarePlanPreviewAtom);
  const setAsyncRxCarePlanPreview = useSetAtom(asyncRxCarePlanPreviewAtom);

  const {
    data: careplanSummary,
    isLoading,
    isFetching,
    isError,
  } = useCarePlanSummary(encounterId, { enabled });

  const { data: chartingAvailableActions } = useChartingAvailableActions({
    patientId,
    motherEncounterId,
  });

  const summary = careplanSummary?.summary;
  const availableActions = careplanSummary?.availableActions;
  const conditionModel = condition ? new Condition(condition) : undefined;

  const createCarePlan = useCreateCarePlan({
    patientId,
    motherEncounterId,
    encounterId,
    isAmazon,
  });

  const toggleReferOut = useToggleReferOut({
    encounterId,
    carePlanId: summary?.careplanId,
  });

  const canWriteReferral = isActionAvailable(
    CareplanAvailableAction.ADD_REFERRAL,
    availableActions,
  );

  const createReferralNotAvailableText = getActionUnavailableReasons(
    CareplanAvailableAction.ADD_REFERRAL,
    availableActions,
  )?.includes(CareplanAvailableActionsUnavailabilityReason.HAS_RX_ORDERS)
    ? "A referral cannot be sent if a prescription has been added."
    : undefined;

  const isAnythingRequiredLoading = isLoading || toggleReferOut.isPending;
  const canCreateSingleCarePlan =
    encounterId === motherEncounterId &&
    careplanSummary !== undefined &&
    !summary;

  const canWriteCarePlan = isActionAvailable(
    ChartingAvailableAction.WRITE_CARE_PLAN,
    chartingAvailableActions,
  );

  const isMotherCarePlanCreateLoading = createCarePlan.isPending || isFetching;

  const clearAmazonCarePlanPreview = useCallback(() => {
    setAmazonCarePlanPreview({
      open: false,
      motherEncounterId: "",
      carePlanId: undefined,
      childEncounterId: "",
      patientId: "",
      condition: null,
    });
  }, [setAmazonCarePlanPreview]);

  useEffect(() => {
    if (summary?.hasAmazonReferOutActivity) {
      setIsReferral(true);
    }
  }, [summary]);

  useEffect(() => {
    if (isAmazon) {
      setAmazonCarePlanPreview({
        open: false,
        carePlanId: summary?.careplanId,
        childEncounterId: encounterId,
        motherEncounterId,
        patientId,
        condition: condition ?? null,
      });
    } else {
      clearAmazonCarePlanPreview();
    }

    return clearAmazonCarePlanPreview;
  }, [
    isAmazon,
    summary?.careplanId,
    encounterId,
    motherEncounterId,
    patientId,
    condition,
    setAmazonCarePlanPreview,
    clearAmazonCarePlanPreview,
  ]);

  useEffect(() => {
    const careplanId = summary?.careplanId;
    if (!isAmazon && careplanId) {
      setAsyncRxCarePlanPreview((previous) => ({
        ...previous,
        carePlanIds: [...new Set([...previous.carePlanIds, careplanId])],
      }));
    }

    return () => {
      if (careplanId) {
        setAsyncRxCarePlanPreview((previous) => ({
          ...previous,
          carePlanIds: previous.carePlanIds.filter((id) => id !== careplanId),
        }));
      }
    };
  }, [isAmazon, summary?.careplanId, setAsyncRxCarePlanPreview]);

  if (!enabled) {
    return null;
  }

  const handleToggleReferOut = async () => {
    if (!canWriteReferral) {
      return;
    }

    try {
      await toggleReferOut.mutateAsync();
      setIsReferral((previous) => !previous);
    } catch {
      toast.error("Failed to toggle refer out. Please try again.");
    }
  };

  const handleClickAddCarePlan = async () => {
    if (createCarePlan.isPending || (canCreateSingleCarePlan && isFetching)) {
      return;
    }

    try {
      await createCarePlan.mutateAsync();
    } catch {
      toast.error("Failed to add new care plan. Please try again.");
    }
  };

  return (
    <div className="flex flex-col gap-2" data-testid="care-plan">
      <div className="flex justify-between items-center">
        <div className="flex flex-col gap-1">
          <div
            className={cn(
              "flex items-center gap-2",
              encounterId === motherEncounterId && "pl-8",
            )}
          >
            {encounterId === motherEncounterId ? (
              <HeaderText size="md" textColor="link">
                Care Plan
              </HeaderText>
            ) : (
              <BodyText size="sm" textColor="primary" bold>
                Care Plan
              </BodyText>
            )}
            {canCreateSingleCarePlan && (
              <Button
                variant="secondary"
                size="xs"
                onClick={handleClickAddCarePlan}
                isLoading={isMotherCarePlanCreateLoading}
                disabled={isMotherCarePlanCreateLoading || !canWriteCarePlan}
              >
                Add Care Plan
              </Button>
            )}
          </div>
          {!isLoading && isAmazon && !summary && (
            <Callout
              title="Follow-up Visit"
              description="This is a follow-up visit related to a prior completed visit. A care plan may not be needed."
              variant="info"
            />
          )}
        </div>

        {isAmazon && summary && (
          <div className="flex items-center gap-2">
            <BodyText size="xs" textColor="primary">
              Refer Out
            </BodyText>
            {toggleReferOut.isPending && (
              <LoaderCircle className="w-3.5 h-3.5 animate-spin" />
            )}
            <Tooltip
              side="top"
              tooltipContentText={createReferralNotAvailableText}
            >
              <Switch
                data-testid="refer-out-switch"
                checked={isReferral}
                onCheckedChange={handleToggleReferOut}
                disabled={toggleReferOut.isPending || !canWriteReferral}
              />
            </Tooltip>
          </div>
        )}
      </div>

      {isError && (
        <Callout
          className="w-full"
          variant="danger"
          title="Failed to load care plan!"
          description="Please refresh and try again."
        />
      )}

      {isAnythingRequiredLoading && (
        <div className="grid gap-2">
          {Array.from({ length: 3 }).map((_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
            <Skeleton className="w-full h-10" key={index} />
          ))}
        </div>
      )}

      {!isAnythingRequiredLoading && summary && (
        <div className="grid gap-1" data-testid="care-plan-summary">
          {!isReferral && (
            <Medications
              patientId={patientId}
              encounterId={encounterId}
              carePlanId={summary.careplanId}
              eocId={episodeOfCareId}
              totalMedications={summary.rxSummaryCount}
              condition={conditionModel}
              isAmazon={isAmazon}
            />
          )}
          <Labs
            patientId={patientId}
            encounterId={encounterId}
            carePlanId={summary.careplanId}
            totalLabs={summary.labSummaryCount}
          />
          <Dispositions
            carePlanId={summary.careplanId}
            encounterId={encounterId}
            totalDispositions={summary.dispositionSummaryCount}
          />
          {isReferral ? (
            toggleReferOut.isPending ? (
              <Skeleton className="w-full h-10" />
            ) : (
              <Referral
                carePlanId={summary.careplanId}
                encounterId={encounterId}
              />
            )
          ) : (
            <Recommendations
              carePlanId={summary.careplanId}
              encounterId={encounterId}
              totalRecommendations={summary.recommendationSummaryCount}
            />
          )}
          <FollowUps
            carePlanId={summary.careplanId}
            childEncounterId={encounterId}
            totalFollowUps={summary.hasFollowUp ? 1 : 0}
          />
        </div>
      )}

      {!isAnythingRequiredLoading && !summary && !canCreateSingleCarePlan && (
        <Button
          variant="secondary"
          className="w-fit"
          size="xs"
          onClick={handleClickAddCarePlan}
          isLoading={createCarePlan.isPending}
          disabled={createCarePlan.isPending || !canWriteCarePlan}
        >
          Add Care Plan
        </Button>
      )}

      {!isAnythingRequiredLoading && summary?.careplanId && (
        <Fragment>
          {!isAmazon && (
            <Button
              size="xs"
              className="w-fit"
              onClick={() => {
                trackEvent(TRACKERS.SEND_CARE_PLAN_SOAP_BUTTON);
                setIsPreviewOpen(true);
              }}
              disabled={
                !isActionAvailable(
                  CareplanAvailableAction.SEND_CARE_PLAN,
                  availableActions,
                )
              }
            >
              Send Care Plan
            </Button>
          )}
          <CarePlanPreviewModal
            patientId={patientId}
            open={isPreviewOpen}
            onOpenChange={setIsPreviewOpen}
            carePlanId={summary.careplanId}
            encounterId={encounterId}
            condition={condition}
          />
        </Fragment>
      )}
    </div>
  );
};
